import React, { useState } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import { makeStyles } from '@material-ui/styles';
import * as Yup from 'yup';
import Fields from 'components/Fields';
import { BoxWrapper, Button } from 'components/Base';
import { PolicyType } from 'store/modules/Policies/types';
import usePolicy from 'hooks/usePolicy';
import { validate } from './validators';
import {
  formatCurrency,
  unFormatPercent,
  unFormatCurrency,
  Notify
} from 'utils';
import api from 'api';
import { ChargeBase, ChargeType } from 'store/modules/AdvisorCharges/types';
import { arrayMove } from 'components/AdvisorChargesV2/utils';
import { generateErrMesage } from 'consts/errorCodes';
import Store from 'store/store';
const { connect, actions } = Store;

const useStyles = makeStyles({
  modal: {
    padding: '1.5rem',
    width: '31rem'
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2rem'
  },
  title: {
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 700
  },
  fieldWrapper: {
    margin: '1rem 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  },
  switcher: {
    marginLeft: '1rem'
  },
  policy: {
    margin: '1rem 0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  amountWrapper: {
    minWidth: '11rem',
    marginLeft: '1rem'
  },
  policiesList: {
    maxHeight: '15rem',
    overflowY: 'scroll'
  }
});

export interface FormValues {
  payment_type: 'regular_contribution' | 'lump_sum';
  charge_in_type: 'monetary' | 'percentage';
  amount: string | null;
  percentage_value: string | null;
  payment_method: 'from_policy' | 'paid_directly';
  applied_charges: {
    policy_id: string | number | null;
  };
}

const validationSchema = Yup.object().shape({
  payment_type: Yup.string().required('Required'),
  charge_in_type: Yup.string(),
  amount: Yup.string().when('charge_in_type', {
    is: (chargeType: string) => chargeType === 'monetary',
    then: Yup.string()
      .required('Required')
      .notOneOf(['£0.00'], 'Value greater than £0.00')
  }),

  percentage_value: Yup.string().when('charge_in_type', {
    is: (chargeType: string) => chargeType === 'percentage',
    then: Yup.string()
      .required('Required')
      .notOneOf(['0.00%'], 'Value greater than 0.00%')
  }),
  payment_method: Yup.string(),
  applied_charges: Yup.object().when('payment_method', {
    is: (payment_method: string) => payment_method === 'from_policy',
    then: Yup.object().shape({
      policy_id: Yup.string().nullable().required('Required')
    })
  })
});

interface Props {
  closeModal: () => void;
  policies: PolicyType[];
  activePolicy: PolicyType;
  portfolio: string;
  isEdit?: boolean;
  initialValues?: FormValues;
  cta: () => Promise<any>;
  editedCharge?: number;
  invalidateStoreSubset: Function;
}

export interface PolicyFormType {
  isChecked: boolean;
  display_name: string;
  id: number;
  fund_value: string;
  charge_allocation_amount: string | number;
}

const AdviserInitialCharges = ({
  closeModal,
  policies,
  activePolicy,
  portfolio,
  cta,
  isEdit,
  initialValues,
  editedCharge,
  invalidateStoreSubset
}: Props) => {
  const originalAppliedPolicy = initialValues?.applied_charges?.policy_id;
  const [isRequestingCharge, setIsRequestingCharge] = useState(false);
  const classes = useStyles();
  const { humanizePolicyProvider, humanizePolicyType } = usePolicy();

  const humanizePolicyName = (
    provider: number,
    type: number,
    number: string,
    ownerFirstName: string
  ) => {
    const humanizedProvider = humanizePolicyProvider(provider);
    const humanizedType = humanizePolicyType(type);

    return `${humanizedProvider} ${humanizedType} \n (${
      number ? number + '-' : ''
    }${ownerFirstName})`;
  };

  const initialPolicies = policies.map((policy: PolicyType) => ({
    isChecked: activePolicy && activePolicy.id === policy.id,
    display_name: humanizePolicyName(
      policy.provider,
      policy.type,
      policy.number,
      policy.owner.first_name
    ),
    id: policy.id,
    fund_value: policy.fund_value,
    charge_allocation_amount: undefined
  }));

  const mainPolicyIndex = initialPolicies.findIndex(policy => policy.isChecked);
  arrayMove(initialPolicies, mainPolicyIndex, 0);
  const isPolicyWithAnnualisedContribution =
    Number(activePolicy.annualised_contribution) !== 0;

  const requestCharge = (values: FormValues) => {
    setIsRequestingCharge(true);
    const {
      amount,
      charge_in_type,
      percentage_value,
      payment_method,
      payment_type,
      applied_charges
    } = values;
    const isPercentageValue = charge_in_type === 'percentage';
    const isRegularCharge = payment_type === 'regular_contribution';

    const chargeValueBaseOnPercentage =
      (unFormatPercent(values.percentage_value || 0) *
        Number(
          isRegularCharge
            ? activePolicy.annualised_contribution
            : activePolicy.fund_value_initial
        )) /
      100;

    const charge: ChargeType = {
      charge_type: isRegularCharge
        ? ChargeBase.INITIAL_REGULAR
        : ChargeBase.INITIAL,
      amount: isPercentageValue ? null : unFormatCurrency(amount),
      percentage_value: isPercentageValue
        ? (Number(unFormatPercent(percentage_value)) / 100).toFixed(7)
        : null,
      applied_charges: []
    };

    if (payment_method === 'paid_directly') {
      charge.applied_charges.push({
        policy_id: null,
        amount: isPercentageValue
          ? chargeValueBaseOnPercentage.toFixed(3)
          : unFormatCurrency(amount),
        paid_directly: true
      });
    } else {
      charge.applied_charges.push({
        ...applied_charges,
        amount: isPercentageValue
          ? chargeValueBaseOnPercentage.toFixed(3)
          : unFormatCurrency(amount),
        paid_directly: false
      });
    }

    if (isEdit && editedCharge) {
      const newAppliedPolicy = charge?.applied_charges?.[0]?.policy_id;

      return api
        .updateCharge({
          portfolio: portfolio,
          chargeId: editedCharge,
          charge: charge
        })
        .then(() => {
          cta().then(() => {
            setIsRequestingCharge(false);
            Notify.success('Charge Edited');

            if (originalAppliedPolicy) {
              invalidateStoreSubset([
                `allAggregateCostAndCharges.${originalAppliedPolicy}`
              ]);
            }

            if (newAppliedPolicy) {
              invalidateStoreSubset([
                `allAggregateCostAndCharges.${newAppliedPolicy}`
              ]);
            }

            closeModal();
          });
        })
        .catch(err => {
          setIsRequestingCharge(false);
          generateErrMesage(err);
        });
    }

    return api
      .requestCharge({
        portfolio: portfolio,
        policyId: activePolicy.id,
        charge: charge
      })
      .then(() => {
        cta().then(() => {
          setIsRequestingCharge(false);
          Notify.success('Charge requested');
          closeModal();
        });
      })
      .catch(err => {
        setIsRequestingCharge(false);
        generateErrMesage(err);
      });
  };

  return (
    <BoxWrapper title=" Initial charge" closeModal={closeModal}>
      <Formik
        initialValues={
          initialValues || {
            payment_type: 'lump_sum',
            payment_method: 'from_policy',
            charge_in_type: 'percentage',
            percentage_value: undefined,
            amount: undefined,
            applied_charges: {
              policy_id: activePolicy.id.toString()
            }
          }
        }
        onSubmit={requestCharge}
        validationSchema={validationSchema}
        validate={(values: any) => validate(values, activePolicy)}
      >
        {({
          handleSubmit,
          values,
          setFieldValue,
          isSubmitting
        }: FormikProps<any>) => {
          const isPercentageValue = values.charge_in_type === 'percentage';
          const isPaidFromPolicies = values.payment_method === 'from_policy';
          const isPaidInRegularContributions =
            values.payment_type === 'regular_contribution';

          const isPaidDirectlyDisabled =
            isPercentageValue && isPaidInRegularContributions;

          const chargeValueBaseOnPercentage =
            (unFormatPercent(values.percentage_value || 0) *
              Number(
                isPaidInRegularContributions
                  ? Number(activePolicy.annualised_contribution)
                  : activePolicy.fund_value_initial
              )) /
            100;

          const paymentTypeOptions = isPolicyWithAnnualisedContribution
            ? [
                { value: 'lump_sum', text: 'Lump sum' },
                {
                  value: 'regular_contribution',
                  text: 'Regular Contribution'
                }
              ]
            : [{ value: 'lump_sum', text: 'Lump sum' }];

          const paymentFromOptions = isPaidDirectlyDisabled
            ? [{ value: 'from_policy', text: 'Paid from policy' }]
            : [
                { value: 'from_policy', text: 'Paid from policy' },
                { value: 'paid_directly', text: 'Paid directly' }
              ];

          const paidFromPolicies = isPaidDirectlyDisabled
            ? initialPolicies.slice(0, 1)
            : initialPolicies;

          return (
            <form onSubmit={handleSubmit} className={classes.modal}>
              <Field
                name="payment_type"
                component={Fields.v2.Switcher}
                label="Charge relates to"
                options={paymentTypeOptions}
                onChange={() => {
                  if (!isPaidDirectlyDisabled) {
                    // Change default value when Paid directly is not supported
                    setFieldValue('payment_method', 'from_policy');
                    setFieldValue(
                      `applied_charges.policy_id`,
                      paidFromPolicies[0].id.toString()
                    );
                  }
                }}
              />
              <div
                className={classes.fieldWrapper}
                style={{
                  marginBottom: isPaidInRegularContributions ? '3rem' : '1rem'
                }}
              >
                {!isPercentageValue && (
                  <Field
                    name="amount"
                    component={Fields.v2.Currency}
                    label="Charge amount"
                    placeholder="£0.00"
                    variant="outlined"
                    labelOnTop
                    size="small"
                    width="100%"
                    helperText={
                      isPaidInRegularContributions &&
                      'This represents a one-off initial adviser monetary charge'
                    }
                    onChange={({
                      formatedValue
                    }: {
                      formatedValue: string;
                    }) => {
                      setFieldValue(
                        `policies[0].charge_allocation_amount`,
                        formatedValue
                      );
                    }}
                  />
                )}
                {isPercentageValue && (
                  <Field
                    name="percentage_value"
                    component={Fields.v2.Percent}
                    label="Charge amount"
                    helperText={
                      isPaidInRegularContributions &&
                      'This represents the % initial adviser charge applied to each ongoing regular contribution'
                    }
                    placeholder="0.00%"
                    variant="outlined"
                    labelOnTop
                    info={`equals ${formatCurrency(
                      chargeValueBaseOnPercentage || 0
                    )}`}
                    size="small"
                    width="100%"
                    onChange={() => {
                      setFieldValue(
                        `policies[0].charge_allocation_amount`,
                        formatCurrency(chargeValueBaseOnPercentage)
                      );
                    }}
                  />
                )}
                <Field
                  name="charge_in_type"
                  component={Fields.v2.Switcher}
                  options={[
                    { value: 'monetary', text: '£' },
                    {
                      value: 'percentage',
                      text: '%'
                    }
                  ]}
                  onChange={() => {
                    if (!isPaidDirectlyDisabled) {
                      // Change default value when Paid directly is not supported
                      setFieldValue('payment_method', 'from_policy');
                      setFieldValue(
                        `applied_charges.policy_id`,
                        paidFromPolicies[0].id.toString()
                      );
                    }
                  }}
                  className={classes.switcher}
                />
              </div>

              <Field
                name="payment_method"
                component={Fields.v2.Switcher}
                label="Payment method"
                options={paymentFromOptions}
              />

              {isPaidFromPolicies && (
                <Field
                  name="applied_charges.policy_id"
                  component={Fields.v2.Radio}
                  options={paidFromPolicies.map(policy => {
                    return {
                      value: policy.id.toString(),
                      label: policy.display_name
                    };
                  })}
                />
              )}

              <div className={classes.footer}>
                <Button color="primary" onClick={() => closeModal()}>
                  Cancel
                </Button>
                <Button
                  isLoading={isSubmitting || isRequestingCharge}
                  type="submit"
                >
                  {isEdit ? 'Update charge' : 'Add charge'}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </BoxWrapper>
  );
};

export default connect(null, {
  invalidateStoreSubset: actions.caseActions.invalidateStoreSubset
})(AdviserInitialCharges);
